<script setup>
import { ref, computed, onMounted } from 'vue';
import DynamicCard from '@/components/DynamicCard.vue';
import TextInput from '@/components/forms/inputs/text-input.vue';
import AdminHeader from '@/pages/admin/AdminHeader.vue';
import BadgesTable from '@/components/table/Table.vue';
import Modal from '@/components/modals/Modal.vue';
import Alert from '@/components/Alert.vue';
import { fetchAllBadges, createBadge, updateBadge, deleteBadge } from '@/pages/admin/user-badges/services/badgesService.js';
import { useToast } from '@/composables/use-toast.js';
import { Cropper } from 'vue-advanced-cropper';

const { addToast } = useToast();
const isLoading = ref(false);

const academicFieldData = ref({});
const showModal = ref(false);
const showEditModal = ref(false);
const showDeleteAlert = ref(false);
const badgeToDelete = ref(null);
const editingBadge = ref(null);
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);

// Cropper states
const showCropper = ref(false);
const imageSource = ref('');
const cropperResult = ref(null);
const badgeImages = ref({});
const editBadgeImage = ref(null);
const currentEditingCardId = ref(null);

// Color picker states
const badgeColors = ref({});
const editBadgeColor = ref('#232323');

// Academic field categories
const academicCategories = ref([]);
const badgeCategories = ref([]);

const badges = ref([]);

// Table columns
const columns = [
    { key: 'name', label: 'Name', type: 'text', sortable: false},
    { key: 'description', label: 'Description', type: 'text', truncate: 50, sortable: false },
    { key: 'user_count', label: 'Users', type: 'number', sortable: false },
    { key: 'created_at', label: 'Created At', type: 'date', sortable: false },
    { key: 'actions', label: 'Actions', type: 'custom', sortable: false },
];

// Filtered badges
const filteredBadges = computed(() => {
    if (!searchQuery.value) return badges.value;

    const query = searchQuery.value.toLowerCase();
    return badges.value.filter(badge =>
        badge.name.toLowerCase().includes(query) ||
        badge.description.toLowerCase().includes(query)
    );
});

const getBadges = async () => {
    const data = await fetchAllBadges();
    if(data) {
        badges.value = data;
    }
}


onMounted(async () => {
    await getBadges();
});

const handleInput = (cardId, field, value) => {
    if (!academicFieldData.value[cardId]) {
        academicFieldData.value[cardId] = {};
    }
    academicFieldData.value[cardId][field] = value;
};

const handleColorChange = (cardId, value) => {
    if (!badgeColors.value[cardId]) {
        badgeColors.value[cardId] = '#232323';
    }
    badgeColors.value[cardId] = value;
};

const openModal = () => {
    showModal.value = true;
    // Initialize default color for new badges
    badgeColors.value = {};
};

const closeModal = () => {
    showModal.value = false;
    academicFieldData.value = {};
    badgeImages.value = {};
    badgeColors.value = {};
    currentEditingCardId.value = null;
};

const validateAllBadges = (badgeEntries) => {
    if (badgeEntries.length === 0) {
        addToast('Please fill all required fields: Name and Description', 'error');
        return false;
    }
    
    let hasValidBadge = false;
    const validationErrors = [];
    
    for (const [cardId, badgeData] of badgeEntries) {
        const badgeErrors = [];
        
        if (!badgeData.name || !badgeData.name.trim()) {
            badgeErrors.push('name');
        }
        
        if (!badgeData.description || !badgeData.description.trim()) {
            badgeErrors.push('description');
        }
        
        if (badgeData.name && badgeData.name.trim()) {
            hasValidBadge = true;
        }
        
        if (badgeErrors.length > 0) {
            validationErrors.push({
                cardId,
                fields: badgeErrors
            });
        }
    }
    
    if (!hasValidBadge) {
        addToast('Please fill the required field: name', 'error');
        return false;
    }
    
    if (validationErrors.length > 0) {
        validationErrors.forEach(({ cardId, fields }) => {
            fields.forEach(field => {
                const fieldLabel = field === 'name' ? 'Field Name' : 'Description';
                addToast(`Please fill the required field: ${fieldLabel}`, 'error');
            });
        });
        return false;
    }
    
    return true;
};

const saveBadges = async () => {
    const badgeEntries = Object.entries(academicFieldData.value);
    
    if (!validateAllBadges(badgeEntries)) {
        return;
    }
    
    isLoading.value = true;
    
    try {
        let allSuccess = true;
        
        for (const [cardId, badgeData] of badgeEntries) {
            if (!badgeData.name) {
                continue;
            }
            
            const result = await createBadge({
                name: badgeData.name,
                description: badgeData.description || '',
                badge_image: badgeImages.value[cardId]?.base64 || null,
                badge_color: badgeColors.value[cardId] || '#232323'
            });
            
            if (!result) {
                allSuccess = false;
                break;
            }
        }
        
        if (allSuccess) {
            addToast('Badge created successfully!', 'success');
            await getBadges();
            closeModal();
        }
    } catch (error) {
        addToast(error?.response?.data?.message || 'Error saving badge', 'error');
    } finally {
        isLoading.value = false;
    }
};

const handleDeleteBadge = (badge) => {
    badgeToDelete.value = badge;
    showDeleteAlert.value = true;
};

const confirmDeleteBadge = async () => {
    if (!badgeToDelete.value) return;
    
    isLoading.value = true;
    
    try {
        const result = await deleteBadge(badgeToDelete.value.id);
        
        if (result) {
            addToast('Badge deleted successfully!', 'success');
            await getBadges();
        }
    } catch (error) {
        addToast(error.response.data.message, 'error');
    } finally {
        isLoading.value = false;
        showDeleteAlert.value = false;
        badgeToDelete.value = null;
    }
};

const cancelDeleteBadge = () => {
    showDeleteAlert.value = false;
    badgeToDelete.value = null;
};

const openEditModal = async (badge) => {
    editingBadge.value = {
        id: badge.id,
        name: badge.name,
        description: badge.description || '',
        badge_image: badge.badge_image || null
    };
    editBadgeColor.value = badge.badge_color || '#232323';
    editBadgeImage.value = null;
    showEditModal.value = true;
};

const closeEditModal = () => {
    showEditModal.value = false;
    editingBadge.value = null;
    editBadgeImage.value = null;
    editBadgeColor.value = '#232323';
};

const handleEditInput = (field, value) => {
    if (editingBadge.value) {
        editingBadge.value[field] = value;
    }
};

const saveEditBadge = async () => {
    if (!editingBadge.value) return;
    
    const validationErrors = [];
    
    if (!editingBadge.value.name || !editingBadge.value.name.trim()) {
        validationErrors.push('name');
    }
    
    if (!editingBadge.value.description || !editingBadge.value.description.trim()) {
        validationErrors.push('description');
    }
    
    if (validationErrors.length > 0) {
        validationErrors.forEach(field => {
            const fieldLabel = field === 'name' ? 'Name' : 'Description';
            addToast(`Please fill the required field: ${fieldLabel}`, 'error');
        });
        return;
    }
        
    isLoading.value = true;
    
    try {

        const badgeImageValue = editBadgeImage.value?.base64 || 
                               (editingBadge.value.badge_image === null ? null : editingBadge.value.badge_image);
        
        const result = await updateBadge(editingBadge.value.id, {
            name: editingBadge.value.name,
            description: editingBadge.value.description,
            badge_image: badgeImageValue,
            badge_color: editBadgeColor.value
        });
        
        if (result) {
            addToast('Badge updated successfully!', 'success');
            await getBadges();
            closeEditModal();
        }
    } catch (error) {
        addToast(error.response.data.message, 'error');
    } finally {
        isLoading.value = false;
    }
};

const handleImageUpload = (event, cardId = null) => {
    const file = event.target.files[0];
    if (file) {
        if (!file.type.startsWith('image/')) {
            addToast('Please select a valid image file', 'error');
            return;
        }

        currentEditingCardId.value = cardId;
        const reader = new FileReader();
        reader.onload = (e) => {
            imageSource.value = e.target.result;
            showCropper.value = true;
        };
        reader.readAsDataURL(file);
    }
};

const onCropperChange = ({ canvas }) => {
    cropperResult.value = canvas;
};

const saveCroppedImage = () => {
    if (cropperResult.value) {
        const maxSize = 400;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const ratio = Math.min(maxSize / cropperResult.value.width, maxSize / cropperResult.value.height);
        
        canvas.width = cropperResult.value.width * ratio;
        canvas.height = cropperResult.value.height * ratio;
        
        ctx.drawImage(cropperResult.value, 0, 0, canvas.width, canvas.height);
        
        const base64Image = canvas.toDataURL('image/jpeg', 0.85);
        
        if (showEditModal.value) {
            editBadgeImage.value = {
                name: 'badge-image.jpg',
                base64: base64Image
            };
        } else if (currentEditingCardId.value) {
            badgeImages.value[currentEditingCardId.value] = {
                name: 'badge-image.jpg',
                base64: base64Image
            };
        }
        
        showCropper.value = false;
        currentEditingCardId.value = null;
    }
};

const cancelCropping = () => {
    showCropper.value = false;
    currentEditingCardId.value = null;
};

const removeSelectedImage = (cardId) => {
    if (badgeImages.value[cardId]) {
        delete badgeImages.value[cardId];
    }
    const fileInput = document.getElementById(`badgeImageInput-${cardId}`);
    if (fileInput) fileInput.value = '';
};

const removeEditImage = () => {
    editBadgeImage.value = null;
    if (editingBadge.value) {
        editingBadge.value.badge_image = null;
    }
    const fileInput = document.getElementById('editBadgeImageInput');
    if (fileInput) fileInput.value = '';
};

const openFileInput = (cardId) => {
    const fileInput = document.getElementById(`badgeImageInput-${cardId}`);
    if (fileInput) fileInput.click();
};

const openEditFileInput = () => {
    const fileInput = document.getElementById('editBadgeImageInput');
    if (fileInput) fileInput.click();
};

const getInitials = (text) => {
    if (!text) return '';
    const words = text.trim().split(/\s+/);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return text.substring(0, 2).toUpperCase();
};
</script>

<template>
    <Teleport to="body">
        <div
            v-if="showCropper"
            class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center"
        >
            <div 
                class="bg-echoes-background-gray border border-echoes-border-light rounded-xl w-full max-w-5xl max-h-[95vh] overflow-hidden p-5 flex flex-col"
                @click.stop
            >
                <div class="relative mb-4 pb-4 -mx-4 px-4 flex-shrink-0">
                    <div class="flex items-center justify-between">
                        <h1 class="text-base 2xl:text-lg font-medium">Crop Badge Image</h1>
                        <button @click="cancelCropping" class="text-lg h-8 w-8 flex items-center justify-center text-white hover:text-red-600 hover:bg-echoes-border-light border border-echoes-border-light hover:none transition-all duration-200 rounded-lg p-2">
                            <i class="uil uil-times"></i>
                        </button>
                    </div>
                    <div class="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-echoes-border-light to-transparent"></div>
                </div>
                <div class="flex-1 overflow-y-auto min-h-0">
                    <div class="cropper-wrapper">
                        <Cropper 
                            ref="cropperRef"
                            :src="imageSource" 
                            @change="onCropperChange" 
                            :stencil-props="{
                                aspectRatio: 1,
                                movable: true,
                                resizable: true,
                                handlers: {
                                    eastNorth: true,
                                    north: true,
                                    westNorth: true,
                                    west: true,
                                    westSouth: true,
                                    south: true,
                                    eastSouth: true,
                                    east: true
                                },
                                handlersClasses: {
                                    default: 'handler-default'
                                },
                                lineWidth: 2
                            }" 
                            class="cropper" 
                        />
                    </div>
                </div>
                <div class="relative flex justify-end gap-3 pt-4 flex-shrink-0">
                    <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-echoes-border-light to-transparent"></div>
                    <button 
                        @click="cancelCropping"
                        class="px-4 py-2 text-sm bg-echoes-background-black hover:bg-echoes-background-black/80 text-white font-medium rounded-lg transition-colors duration-200 border border-echoes-border-light"
                    >
                        Cancel
                    </button>
                    <button 
                        @click="saveCroppedImage"
                        class="px-4 py-2 text-sm bg-echoes-primary hover:bg-echoes-primary/80 text-white font-medium rounded-lg transition-colors duration-200"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    </Teleport>

    <div class="p-5 bg-echoes-background-black min-h-screen">
        <AdminHeader>
            <template #title>User Badges Management</template>
            <template #description>
                Create and manage user badges for the platform.
            </template>
            <template #actions>
                <div class="flex justify-end">
                    <button @click="openModal"
                        class="flex items-center gap-2 px-6 py-2.5 bg-echoes-primary hover:bg-echoes-primary/80 text-white font-medium rounded-lg transition-colors duration-200">
                        <i class="uil uil-plus text-lg"></i>
                        <span>Add New Badge</span>
                    </button>
                </div>
            </template>
        </AdminHeader>

        <!-- Table -->
        <BadgesTable :data="filteredBadges" :columns="columns" :total-items="filteredBadges.length"
            :search-query="searchQuery" @update:search-query="searchQuery = $event" :current-page="currentPage"
            @update:current-page="currentPage = $event" :items-per-page="itemsPerPage"
            @update:items-per-page="itemsPerPage = $event"
            search-placeholder="Search by name or description..." empty-message="No badges found">
            <!-- Custom Name Column with Image -->
            <template #column-name="{ value, item }">
                <div class="flex items-center gap-3">
                    <img v-if="item.badge_image" :src="item.badge_image" :alt="value" class="w-10 h-10 rounded-full object-cover" />
                    <div v-else 
                        class="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                        :style="{ backgroundColor: item.badge_color || '#232323' }">
                        {{ getInitials(value) }}
                    </div>
                    <span>{{ value }}</span>
                </div>
            </template>

    
            
            
            <!-- Actions Column -->
            <template #column-actions="{ item }">
                <div class="flex items-center  space-x-4">
                    <button 
                        @click="openEditModal(item)"
                        :disabled="isLoading"
                        class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light disabled:opacity-50"
                    >
                        <i class="uil uil-pen mr-2"></i>
                        Edit
                    </button>
                    <button 
                        @click="handleDeleteBadge(item)"
                        :disabled="isLoading"
                        class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light disabled:opacity-50"
                    >
                        <i class="uil uil-trash mr-2"></i>
                        Delete
                    </button>
                </div>
            </template>
        </BadgesTable>

        <!-- Modal with DynamicCard -->
        <Modal 
            :is-open="showModal" 
            @close="closeModal" 
            @confirm="saveBadges"
            @cancel="closeModal"
            title="Create Badge" 
            size="3xl" 
            :show-footer="true" 
            :close-on-click-outside="false"
            :is-loading="isLoading"
            confirm-button-text="Save Badge"
            loading-text="Saving..."
        >
            <div class="space-y-6">
                <DynamicCard single-card disable-collapsible>
                    <template #default="{ card, index }">
                        <div class="w-full flex flex-col gap-4">
                            <!-- Badge Image Upload per card -->
                            <div class="w-full">
                                <label :for="`badgeImage-${card.id}`" class="block text-white/80 text-sm font-medium mb-3">
                                    Badge Image
                                </label>
                                <div class="relative group">
                                    <!-- Image Preview Area -->
                                    <div v-if="badgeImages[card.id]" 
                                        class="relative w-full rounded-xl bg-gradient-to-br from-echoes-background-black/60 to-echoes-background-black border border-echoes-border-light overflow-hidden">
                                        <div class="flex items-center gap-3 p-3">
                                            <!-- Image Preview -->
                                            <div class="relative flex-shrink-0">
                                                <div class="w-16 h-16 rounded-full overflow-hidden border border-echoes-border-light">
                                                    <img :src="badgeImages[card.id].base64" alt="Badge preview" class="w-full h-full object-cover" />
                                                </div>
                                            </div>
                                            
                                            <!-- Image Info & Actions -->
                                            <div class="flex-1 min-w-0">
                                                <div class="flex items-start justify-between gap-3">
                                                    <div class="flex-1 min-w-0">
                                                        <p class="text-white font-medium text-xs mb-0.5 truncate">{{ badgeImages[card.id].name }}</p>
                                                        <p class="text-white/50 text-xs">Image uploaded successfully</p>
                                                    </div>
                                                    <button 
                                                        @click="removeSelectedImage(card.id)" 
                                                        class="flex-shrink-0 w-7 h-7 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 flex items-center justify-center"
                                                        title="Remove image"
                                                    >
                                                        <i class="uil uil-times text-sm"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Upload Button (shown when no image) -->
                                    <button 
                                        v-else
                                        @click="openFileInput(card.id)" 
                                        class="w-full relative rounded-xl bg-gradient-to-br from-echoes-background-black/60 to-echoes-background-black border-2 border-dashed border-echoes-border-light overflow-hidden"
                                        :disabled="isLoading"
                                        type="button"
                                    >
                                        <div class="relative p-4 flex flex-col items-center justify-center gap-2">
                                            <div class="w-12 h-12 rounded-xl bg-echoes-primary/10 flex items-center justify-center">
                                                <svg class="w-6 h-6 text-echoes-primary" viewBox="0 0 94 94" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                        stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </div>
                                            <div class="text-center">
                                                <p class="text-white font-medium text-xs mb-0.5">Click to upload</p>
                                                <p class="text-white/50 text-xs">PNG, JPG, WEBP up to 5MB</p>
                                            </div>
                                        </div>
                                    </button>
                                    
                                    <input 
                                        :id="`badgeImageInput-${card.id}`" 
                                        type="file" 
                                        @change="handleImageUpload($event, card.id)"
                                        class="hidden" 
                                        accept="image/*" 
                                    />
                                </div>
                            </div>
                     

                            <div class="w-full flex gap-4">
                                <div class="flex-shrink-0">
                                    <label :for="`badge-color-${card.id}`" class="block text-white/80 text-sm mb-2">
                                        Badge Color
                                    </label>
                                    <div class="flex items-center gap-4">
                                        <input
                                            type="color"
                                            :id="`badge-color-${card.id}`"
                                            :value="badgeColors[card.id] || '#232323'"
                                            @input="handleColorChange(card.id, $event.target.value)"
                                            class="h-10 w-20 cursor-pointer border-none bg-transparent"
                                        >
                                        <div class="flex w-[96px]">
                                            <TextInput 
                                                :autocomplete="'off'" 
                                                :id="`badge-color-input-${card.id}`" 
                                                :model-value="badgeColors[card.id] || '#232323'"
                                                @update:model-value="handleColorChange(card.id, $event)"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-1">
                                    <label :for="`field-name-${card.id}`" class="block text-white/80 text-sm mb-2">
                                        Name
                                    </label>
                                    <TextInput :id="`field-name-${card.id}`"
                                        :model-value="academicFieldData[card.id]?.name || ''"
                                        @update:model-value="handleInput(card.id, 'name', $event)"
                                        placeholder="e.g., Computer Science, Mathematics, Physics..." />
                                </div>

                              
                            </div>

                            <div class="w-full">
                                <label :for="`field-description-${card.id}`" class="block text-white/80 text-sm mb-2">
                                    Description
                                </label>
                                <TextInput :id="`field-description-${card.id}`"
                                    :model-value="academicFieldData[card.id]?.description || ''"
                                    @update:model-value="handleInput(card.id, 'description', $event)"
                                    placeholder="Describe this academic field..." :is-textarea="true" :rows="3" />
                            </div>
                        </div>
                    </template>
                </DynamicCard>
            </div>
        </Modal>

        <!-- Edit Modal -->
        <Modal 
            :is-open="showEditModal" 
            @close="closeEditModal" 
            @confirm="saveEditBadge"
            @cancel="closeEditModal"
            title="Edit Badge" 
            size="xl" 
            :show-footer="true" 
            :close-on-click-outside="false"
            :is-loading="isLoading"
            confirm-button-text="Save Changes"
            loading-text="Saving..."
        >
            <div class="space-y-6" v-if="editingBadge">
                <!-- Badge Image Upload -->
                <div class="w-full">
                    <label for="editBadgeImage" class="block text-white/80 text-sm font-medium mb-3">
                        Badge Image
                    </label>
                    <div class="relative group">
                        <!-- Image Preview Area -->
                        <div v-if="editBadgeImage || editingBadge.badge_image" 
                            class="relative w-full rounded-xl bg-gradient-to-br from-echoes-background-black/60 to-echoes-background-black border border-echoes-border-light overflow-hidden">
                            <div class="flex items-center gap-3 p-3">
                                <!-- Image Preview -->
                                <div class="relative flex-shrink-0">
                                    <div class="w-16 h-16 rounded-full overflow-hidden border border-echoes-border-light">
                                        <img :src="editBadgeImage?.base64 || editingBadge.badge_image" alt="Badge preview" class="w-full h-full object-cover" />
                                    </div>
                                </div>
                                
                                <!-- Image Info & Actions -->
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-white font-medium text-xs mb-0.5 truncate">
                                                {{ editBadgeImage?.name || 'Current image' }}
                                            </p>
                                            <p class="text-white/50 text-xs">Image uploaded successfully</p>
                                        </div>
                                        <button 
                                            @click="removeEditImage" 
                                            class="flex-shrink-0 w-7 h-7 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 flex items-center justify-center"
                                            title="Remove image"
                                        >
                                            <i class="uil uil-times text-sm"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Upload Button (shown when no image) -->
                        <button 
                            v-else
                            @click="openEditFileInput()" 
                            class="w-full relative rounded-xl bg-gradient-to-br from-echoes-background-black/60 to-echoes-background-black border-2 border-dashed border-echoes-border-light overflow-hidden"
                            :disabled="isLoading"
                            type="button"
                        >
                            <div class="relative p-4 flex flex-col items-center justify-center gap-2">
                                <div class="w-12 h-12 rounded-xl bg-echoes-primary/10 flex items-center justify-center">
                                    <svg class="w-6 h-6 text-echoes-primary" viewBox="0 0 94 94" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                            stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </div>
                                <div class="text-center">
                                    <p class="text-white font-medium text-xs mb-0.5">Click to upload</p>
                                    <p class="text-white/50 text-xs">PNG, JPG, WEBP up to 5MB</p>
                                </div>
                            </div>
                        </button>
                        
                        <input 
                            type="file" 
                            id="editBadgeImageInput" 
                            @change="handleImageUpload($event, null)"
                            class="hidden" 
                            accept="image/*" 
                        />
                    </div>
                </div>

              

                <div class="w-full flex gap-4">
                    <div class="flex-shrink-0">
                        <label for="edit-badge-color" class="block text-white/80 text-sm mb-2">
                            Badge Color
                        </label>
                        <div class="flex items-center gap-4">
                            <input
                                type="color"
                                id="edit-badge-color"
                                v-model="editBadgeColor"
                                class="h-10 w-20 cursor-pointer border-none bg-transparent"
                            >
                            <div class="flex w-[96px]">
                                <TextInput 
                                    :autocomplete="'off'" 
                                    id="edit-badge-color-input" 
                                    v-model="editBadgeColor"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="flex-1">
                        <label for="edit-field-name" class="block text-white/80 text-sm mb-2">
                            Name
                        </label>
                        <TextInput 
                            id="edit-field-name"
                            :model-value="editingBadge.name"
                            @update:model-value="handleEditInput('name', $event)"
                            placeholder="e.g., Computer Science, Mathematics, Physics..." 
                        />
                    </div>
                </div>

                <div class="w-full">
                    <label for="edit-description" class="block text-white/80 text-sm mb-2">
                        Description
                    </label>
                    <TextInput 
                        id="edit-description"
                        :model-value="editingBadge.description"
                        @update:model-value="handleEditInput('description', $event)"
                        placeholder="Describe this academic field..." 
                        :is-textarea="true" 
                        :rows="3" 
                    />
                </div>
            </div>
        </Modal>

        <!-- Delete Confirmation Alert -->
        <Alert
            :show="showDeleteAlert"
            type="danger"
            title="Confirm Delete"
            :message="`Are you sure you want to delete the badge <strong>&quot;${badgeToDelete?.name}&quot;</strong>?<br><br>This action cannot be undone.`"
            confirm-text="Delete"
            cancel-text="Cancel"
            :is-loading="isLoading"
            loading-text="Deleting..."
            @confirm="confirmDeleteBadge"
            @close="cancelDeleteBadge"
        />
    </div>
</template>

<style scoped>
.cropper-wrapper {
    min-height: 500px;
    height: calc(95vh - 200px);
    max-height: 800px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
}

.cropper {
    background-color: #18181b;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
}

.handler-default {
    width: 12px;
    height: 12px;
    background-color: #8B5CF6;
    border-radius: 50%;
}

</style>